<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
      icon: { required },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
        },
        position: 'messages', // 'messages' or 'sidebar' - default to conversation
        icon: 'i-lucide-external-link', // Adicionando campo para ícone
      },
      // Lista de ícones disponíveis para seleção (apenas os comprovadamente funcionais)
      availableIcons: [
        // Navegação e links
        'i-lucide-external-link',
        'i-lucide-link',
        'i-lucide-arrow-up-right',

        // Dispositivos e tecnologia
        'i-lucide-monitor',
        'i-lucide-laptop',
        'i-lucide-tablet',
        'i-lucide-smartphone',
        'i-lucide-phone',

        // Comunicação
        'i-lucide-message-circle',
        'i-lucide-mail',
        'i-lucide-inbox',

        // Arquivos e documentos
        'i-lucide-folder',
        'i-lucide-file',
        'i-lucide-book-open',

        // Mídia
        'i-lucide-image',
        'i-lucide-video',
        'i-lucide-music',

        // Tempo e organização
        'i-lucide-calendar',
        'i-lucide-clock',
        'i-lucide-heart',

        // Ferramentas e configurações
        'i-lucide-settings',
        'i-lucide-wrench',
        'i-lucide-cog',

        // Usuários e equipes
        'i-lucide-user',
        'i-lucide-users',
        'i-lucide-building',

        // Localização
        'i-lucide-home',
        'i-lucide-map-pin',

        // Marcadores e tags
        'i-lucide-flag',
        'i-lucide-tag',
        'i-lucide-bookmark',

        // Notificações
        'i-lucide-bell',
        'i-lucide-alert-circle',
        'i-lucide-info',
        'i-lucide-help-circle',

        // Ações comuns
        'i-lucide-plus',
        'i-lucide-minus',
        'i-lucide-x',
        'i-lucide-check',
        'i-lucide-edit',
        'i-lucide-trash',
        'i-lucide-archive',
        'i-lucide-download',
        'i-lucide-upload',
        'i-lucide-share',
        'i-lucide-copy',
        'i-lucide-search',

        // Setas e navegação
        'i-lucide-chevron-left',
        'i-lucide-chevron-right',
        'i-lucide-chevron-up',
        'i-lucide-chevron-down',
        'i-lucide-arrow-left',
        'i-lucide-arrow-right',
        'i-lucide-arrow-up',
        'i-lucide-arrow-down',

        // Gráficos e dados
        'i-lucide-bar-chart',
        'i-lucide-pie-chart',
        'i-lucide-line-chart',
        'i-lucide-activity',
        'i-lucide-trending-up',
        'i-lucide-trending-down',

        // Status e indicadores
        'i-lucide-circle',
        'i-lucide-check-circle',
        'i-lucide-x-circle',
        'i-lucide-alert-triangle',

        // Especiais
        'i-lucide-sparkles',
        'i-lucide-zap',
        'i-lucide-target',
        'i-lucide-gift',
        'i-lucide-award',
        'i-lucide-trophy',

        // Transporte e aviação
        'i-lucide-plane',
        'i-lucide-plane-takeoff',
        'i-lucide-plane-landing',
        'i-lucide-ship',
        'i-lucide-train',
        'i-lucide-car',
        'i-lucide-bike',
        'i-lucide-bus',
        'i-lucide-truck',

        // Foguetes e espaço
        'i-lucide-rocket',
        'i-lucide-satellite',
        'i-lucide-moon',
        'i-lucide-sun',
        'i-lucide-star',

        // Adicionais comprovadamente funcionais
        'i-lucide-globe',
        'i-lucide-pen-line',
        'i-lucide-mailbox',
        'i-lucide-life-buoy',
        'i-lucide-keyboard',
        'i-lucide-user-pen',
        'i-lucide-swatch-book',
        'i-lucide-castle',
        'i-lucide-log-out',
        'i-lucide-lock-keyhole',
        'i-lucide-credit-card',
        'i-lucide-pipette',
        'i-lucide-smile-plus',
        'i-lucide-shapes',
        'i-lucide-ellipsis-vertical',
        'i-lucide-eye',
        'i-lucide-cloud-download',
        'i-lucide-chevrons-down',
        'i-lucide-chevrons-up',
        'i-lucide-library-big',
        'i-lucide-messages-square',
        'i-lucide-alarm-clock-plus',
        'i-lucide-alarm-clock-off',
        'i-lucide-refresh-ccw',
        'i-lucide-check-check',
      ],
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
  },
  mounted() {
    if (this.mode.toUpperCase() === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.app.position = this.selectedAppData.position || 'messages';
      this.app.icon = this.selectedAppData.icon || 'i-lucide-external-link'; // Carregando o ícone
    }
  },
  methods: {
    closeModal() {
      // Reset the data once closed
      this.resetForm();
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
          position: this.app.position,
          icon: this.app.icon, // Incluindo o ícone no payload
        };

        if (this.mode.toUpperCase() === 'UPDATE') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
    selectIcon(icon) {
      this.app.icon = icon;
    },
    resetForm() {
      this.app = {
        title: '',
        content: { type: 'frame', url: '' },
        position: 'messages', // Default to conversation
        icon: 'i-lucide-external-link', // Resetando o ícone
      };
      this.v$.$reset(); // Reset all validation rules
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full space-y-4" @submit.prevent="submit">
        <woot-input
          v-model="app.title"
          :class="{ error: v$.app.title.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <woot-input
          v-model="app.content.url"
          :class="{ error: v$.app.content.url.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />

        <!-- Seleção de ícone -->
        <div class="w-full">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_LABEL') }}
          </label>
          <div
            class="grid grid-cols-8 gap-2 max-h-40 overflow-y-auto p-2 border border-n-weak rounded-lg"
          >
            <button
              v-for="icon in availableIcons"
              :key="icon"
              type="button"
              class="flex items-center justify-center w-10 h-10 rounded-lg border-2 transition-all duration-200 hover:bg-n-alpha-2"
              :class="{
                'border-n-brand bg-n-brand/10': app.icon === icon,
                'border-n-weak hover:border-n-strong': app.icon !== icon,
              }"
              :title="icon.replace('i-lucide-', '')"
              @click="selectIcon(icon)"
            >
              <span :class="icon" class="text-lg" />
            </button>
          </div>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_HELP') }}
          </p>
        </div>

        <div class="w-full">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_LABEL') }}
          </label>
          <div class="space-y-2">
            <label class="flex items-center space-x-2 cursor-pointer">
              <input
                v-model="app.position"
                type="radio"
                value="messages"
                class="text-n-iris-9 focus:ring-n-iris-9"
              />
              <span class="text-sm">{{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_MESSAGES'
                ) || 'Conversation'
              }}</span>
            </label>
            <label class="flex items-center space-x-2 cursor-pointer">
              <input
                v-model="app.position"
                type="radio"
                value="sidebar"
                class="text-n-iris-9 focus:ring-n-iris-9"
              />
              <span class="text-sm">{{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.POSITION_SIDEBAR'
                ) || 'Sidebar'
              }}</span>
            </label>
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
